import { useEffect, useState } from 'react'
import {
  ArrowRight,
  Boxes,
  Check,
  CloudCog,
  Database,
  Download,
  Menu,
  ShieldCheck,
  Sparkles,
  Workflow,
  X,
} from 'lucide-react'
import { products } from './data/products'

const navItems = [
  { label: '平台', href: '#platform' },
  { label: '智能体', href: '#agents' },
  { label: '平台演示', href: '#demo' },
  { label: '成本测算', href: '#cost-estimate' },
  { label: '下载', href: '#download' },
]

function BrandMark() {
  return (
    <span className="brand-mark" aria-hidden="true">
      <i />
      <i />
      <i />
    </span>
  )
}

export function App() {
  const [menuOpen, setMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <div className="site-shell">
      <header className={`site-header ${scrolled ? 'is-scrolled' : ''}`}>
        <a className="brand" href="#top" aria-label="筑翎首页">
          <BrandMark />
          <span className="brand-copy">
            <strong>筑翎</strong>
            <small>AEC Agent</small>
          </span>
        </a>

        <nav className="desktop-nav" aria-label="主导航">
          {navItems.map((item) => (
            <a key={item.href} href={item.href}>
              {item.label}
            </a>
          ))}
        </nav>

        <div className="header-actions">
          <a className="text-action" href="#agents">
            产品目录
          </a>
          <a className="primary-action compact" href="#download">
            申请内测
            <ArrowRight size={15} />
          </a>
        </div>

        <button
          className="menu-button"
          type="button"
          aria-label={menuOpen ? '关闭导航' : '打开导航'}
          onClick={() => setMenuOpen((value) => !value)}
        >
          {menuOpen ? <X size={20} /> : <Menu size={20} />}
        </button>

        {menuOpen && (
          <nav className="mobile-nav" aria-label="移动端导航">
            {navItems.map((item) => (
              <a key={item.href} href={item.href} onClick={() => setMenuOpen(false)}>
                {item.label}
                <ArrowRight size={16} />
              </a>
            ))}
          </nav>
        )}
      </header>

      <main>
        <section className="hero" id="top">
          <img
            className="hero-image"
            src="/images/cost-agent-collaboration-poster.png"
            alt="筑翎智能成本测算工作台"
          />
          <div className="hero-shade" />
          <div className="hero-content page-width">
            <div className="hero-copy">
              <span className="eyebrow">工程建设智能体平台</span>
              <h1>筑翎</h1>
              <p>
                让企业标准、项目数据与专业 Agent 在同一工作流中协同。
                确定性工具先完成基础测算，业务人员只处理真正需要判断的事项。
              </p>
              <div className="hero-actions">
                <a className="primary-action" href="#agents">
                  探索智能体
                  <ArrowRight size={17} />
                </a>
                <a className="secondary-action" href="#platform">
                  了解平台
                </a>
              </div>
            </div>
            <div className="hero-status" aria-label="平台能力摘要">
              <span><Check size={15} /> 真实项目工作台</span>
              <span><Check size={15} /> Agent 按专业并行检查</span>
              <span><Check size={15} /> 人工确认后生效</span>
            </div>
          </div>
        </section>

        <section className="platform-section" id="platform">
          <div className="page-width platform-layout">
            <div className="section-heading">
              <span className="eyebrow dark">平台方式</span>
              <h2>专业能力，不止一次回答</h2>
              <p>
                筑翎把模型、工具、企业资产和业务界面组合成可运行的专业智能体，
                让复杂任务在清晰的工作流中被执行、复核和沉淀。
              </p>
            </div>

            <div className="capability-grid">
              <article>
                <Workflow size={22} />
                <h3>任务工作流</h3>
                <p>围绕真实业务对象组织输入、推理、工具调用、人工确认与成果交付。</p>
              </article>
              <article>
                <Database size={22} />
                <h3>企业知识资产</h3>
                <p>标准、规则、案例和价格依据按租户加载，版本可追溯、可撤回、可回滚。</p>
              </article>
              <article>
                <ShieldCheck size={22} />
                <h3>本地数据边界</h3>
                <p>项目原始文件优先在客户端处理，云端集中管理授权、模型调用和公共资产。</p>
              </article>
              <article>
                <CloudCog size={22} />
                <h3>持续发布</h3>
                <p>智能体与资产在线更新，企业可以持续获得经过验证的新能力。</p>
              </article>
            </div>
          </div>
        </section>

        <section className="agents-section" id="agents">
          <div className="page-width">
            <div className="section-row">
              <div className="section-heading compact-heading">
                <span className="eyebrow dark">智能体目录</span>
                <h2>从专业工作台进入</h2>
              </div>
              <p className="section-note">同一个平台，按任务安装所需能力。</p>
            </div>

            <div className="product-grid">
              {products.map((product, index) => {
                const Icon = product.icon
                return (
                  <article className={`product-card ${index === 0 ? 'featured' : ''}`} key={product.id}>
                    <div className="product-card-top">
                      <span className="product-icon" style={{ color: product.accent, backgroundColor: `${product.accent}12` }}>
                        <Icon size={22} strokeWidth={1.8} />
                      </span>
                      <span className="product-status">{product.status}</span>
                    </div>
                    <span className="product-category">{product.category}</span>
                    <h3>{product.name}</h3>
                    <p>{product.summary}</p>
                    <a href={product.id === 'cost-estimate' ? '#cost-estimate' : '#download'}>
                      查看产品
                      <ArrowRight size={15} />
                    </a>
                  </article>
                )
              })}
            </div>
          </div>
        </section>

        <section className="demo-section" id="demo">
          <div className="page-width">
            <div className="demo-heading">
              <div>
                <span className="eyebrow">筑翎平台总览</span>
                <h2>75 秒了解工程建设智能体平台</h2>
              </div>
              <p>
                专业智能体共享统一的平台底座，连接企业数据、可复用技能与真实工作流程。
                过程可以追问和接管，成果可以复核与交付。
              </p>
            </div>

            <div className="demo-layout">
              <figure className="demo-player">
                <video
                  controls
                  playsInline
                  preload="metadata"
                  poster="/images/zhuling-platform-overview-poster.png"
                >
                  <source src="/images/zhuling-platform-overview.mp4" type="video/mp4" />
                  当前浏览器不支持视频播放。
                </video>
                <figcaption>带解说的平台能力总览，演示专业智能体从任务进入到成果交付的完整过程。</figcaption>
              </figure>

              <div className="demo-side">
                <ol className="demo-steps">
                  <li>
                    <span>01</span>
                    <div><strong>专业智能体</strong><p>围绕合同、成本、图纸和项目协同等明确任务提供工作台。</p></div>
                  </li>
                  <li>
                    <span>02</span>
                    <div><strong>企业能力接入</strong><p>连接项目文件、企业数据源和可复用技能，不把知识锁在提示词里。</p></div>
                  </li>
                  <li>
                    <span>03</span>
                    <div><strong>人机共同负责</strong><p>智能体展示过程并生成成果，关键决策由业务人员确认与接管。</p></div>
                  </li>
                </ol>
                <figure className="demo-detail">
                  <img src="/images/zhuling-platform-agents.png" alt="筑翎平台中的专业智能体目录" />
                  <figcaption>专业智能体按需装配，共享统一的会话、权限、数据源、技能与审计能力。</figcaption>
                </figure>
              </div>
            </div>
          </div>
        </section>

        <section className="cost-section" id="cost-estimate">
          <div className="page-width cost-layout">
            <div className="cost-copy">
              <span className="eyebrow dark">智能成本测算</span>
              <h2>确定的交给工具，不确定的交给 Agent</h2>
              <p>
                导入收入清单后，系统按工程专业、构造层和企业责任口径形成成本组合。
                Agent 只检查缺项、边界与标准库覆盖，不推翻已经确认的结果。
              </p>
              <ul>
                <li><Check size={17} /> 清单、定额与工料机证据统一解析</li>
                <li><Check size={17} /> 劳务、材料和专业分包形成 0-N 成本组合</li>
                <li><Check size={17} /> 按专业并行检查，缺口定位到具体收入行</li>
                <li><Check size={17} /> 人工补充后继续运行并保留依据</li>
              </ul>
              <a className="inline-action" href="#download">
                申请参与内测
                <ArrowRight size={16} />
              </a>
            </div>
            <figure className="product-shot">
              <img src="/images/cost-agent-overview.png" alt="Agent 按专业并行检查成本测算结果" />
              <figcaption>Agent 按专业检查缺口，不推翻已确认的成本草案。</figcaption>
            </figure>
          </div>
        </section>

        <section className="delivery-section" id="download">
          <div className="page-width delivery-layout">
            <div>
              <span className="eyebrow dark">筑翎客户端</span>
              <h2>一个入口，按需获得专业能力</h2>
              <p>
                Windows 客户端面向首批内测用户开放。安装筑翎后，后续智能体和企业资产将通过平台持续发布。
              </p>
            </div>
            <div className="download-panel">
              <div className="download-meta">
                <span className="platform-badge">Windows x64</span>
                <strong>筑翎内测版</strong>
                <small>由团队分配账号与产品权限</small>
              </div>
              <a className="primary-action download-action" href="mailto:hello@aecagent.com?subject=申请筑翎内测">
                <Download size={17} />
                申请内测
              </a>
            </div>
          </div>
        </section>

        <section className="principles-section">
          <div className="page-width principles-layout">
            <div className="section-heading compact-heading">
              <span className="eyebrow dark">面向企业</span>
              <h2>把能力建设留在组织里</h2>
            </div>
            <div className="principle-list">
              <div><Boxes size={19} /><span>模块化智能体</span></div>
              <div><Database size={19} /><span>租户级资产</span></div>
              <div><ShieldCheck size={19} /><span>可治理发布</span></div>
              <div><Sparkles size={19} /><span>持续学习</span></div>
            </div>
          </div>
        </section>
      </main>

      <footer>
        <div className="page-width footer-layout">
          <div className="footer-brand">
            <BrandMark />
            <div><strong>筑翎</strong><span>AEC Agent</span></div>
          </div>
          <p>面向工程建设行业的企业级智能体平台</p>
          <div className="footer-links">
            <a href="#agents">产品</a>
            <a href="#platform">平台</a>
            <a href="mailto:hello@aecagent.com">联系</a>
          </div>
          <small>© 2026 筑翎 AEC Agent</small>
        </div>
      </footer>
    </div>
  )
}
